import { supabase } from "@/integrations/supabase/client";

export type PublicSiteSettings = {
  og_image_url: string | null;
  google_verification: string | null;
  bing_verification: string | null;
  ga_id: string | null;
};

const FALLBACK_PUBLIC_SITE_SETTINGS: PublicSiteSettings = {
  og_image_url: null,
  google_verification: null,
  bing_verification: null,
  ga_id: null,
};

// Small in-memory TTL cache. Safe on both client and server (per-process/per-tab).
// On server (Worker), this de-dupes reads within a single isolate's lifetime.
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
let cached: { value: PublicSiteSettings; expiresAt: number } | null = null;
let inflight: Promise<PublicSiteSettings> | null = null;

export function invalidatePublicSiteSettingsCache() {
  cached = null;
  inflight = null;
}

async function fetchPublicSiteSettings(): Promise<PublicSiteSettings> {
  try {
    // site_settings_public is a curated view exposing only non-sensitive fields.
    const { data, error } = await (supabase as unknown as {
      from: (t: string) => {
        select: (cols: string) => { maybeSingle: () => Promise<{ data: PublicSiteSettings | null; error: unknown }> };
      };
    })
      .from("site_settings_public")
      .select("og_image_url, google_verification, bing_verification, ga_id")
      .maybeSingle();

    if (error) throw error;

    return {
      og_image_url: data?.og_image_url ?? null,
      google_verification: data?.google_verification ?? null,
      bing_verification: data?.bing_verification ?? null,
      ga_id: data?.ga_id ?? null,
    };
  } catch (e) {
    console.error("getPublicSiteSettings failed", e);
    return FALLBACK_PUBLIC_SITE_SETTINGS;
  }
}

export async function getPublicSiteSettings(): Promise<PublicSiteSettings> {
  const now = Date.now();
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }
  if (inflight) return inflight;

  inflight = fetchPublicSiteSettings()
    .then((value) => {
      cached = { value, expiresAt: Date.now() + CACHE_TTL_MS };
      return value;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}
